import { type CommonTemplateProps } from '#app/components/Modals/V2/GlobalModalSystem/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { theme } from '#app/theme/theme.js';
import { ModalTemplateWrapper } from '../ModalTemplateWrapper/ModalTemplateWrapper.js';
import { AlertCircleIcon } from '@contra/icons/icons';
import { useCallback } from 'react';

type Props = CommonTemplateProps & {
  readonly errorType: 'OTHER' | 'RATE_LIMIT';
  readonly onDismiss: () => void;
  readonly onRetry: () => void;
};

export const ProfileMagicInputErrorModal = ({
  closeModal,
  errorType,
  isOpen,
  onDismiss,
  onRetry,
}: Props) => {
  const { track } = useAnalytics();

  const handleRetry = useCallback(() => {
    onRetry();
    track('button', {
      action: 'tapped',
      button_name: 'growth_profile_magic_error_modal_try_again',
    });
  }, [onRetry, track]);

  const handleClose = useCallback(() => {
    closeModal();
    onDismiss();
    track('modal', {
      action: 'tapped',
      cta: 'close',
      modal_name: 'growth_profile_magic_questions_input_error',
    });
  }, [closeModal, onDismiss, track]);

  const handleCancel = useCallback(() => {
    closeModal();
    onDismiss();
    track('button', {
      action: 'tapped',
      button_name: 'growth_profile_magic_error_modal_cancel',
    });
  }, [closeModal, onDismiss, track]);

  const handleAcceptRateLimitError = useCallback(() => {
    closeModal();
    track('button', {
      action: 'tapped',
      button_name: 'growth_profile_magic_error_modal_ok',
    });
  }, [closeModal, track]);

  const ctaOptions =
    errorType === 'RATE_LIMIT'
      ? {
          primaryCTA: {
            handleOnClick: handleAcceptRateLimitError,
            label: 'Okay',
          },
        }
      : {
          primaryCTA: {
            handleOnClick: handleRetry,
            label: 'Try again',
          },
          secondaryCTA: {
            handleOnClick: handleCancel,
            label: 'Cancel',
          },
        };

  const heading =
    errorType === 'RATE_LIMIT'
      ? 'We’re experiencing a lot of requests'
      : 'Sorry, something went wrong';

  const subHeading =
    errorType === 'RATE_LIMIT'
      ? 'We weren’t able to generate content. Please try again later.'
      : 'We weren’t able to generate content. Please try again!';

  return (
    <ModalTemplateWrapper
      ariaLabel="Portfolio Magic Questionnaire Error"
      ctaOptions={ctaOptions}
      desktopModalSize="md"
      headerText={heading}
      icon={
        <AlertCircleIcon
          color={theme.colorsV2.red50}
          size={24}
        />
      }
      isOpen={isOpen}
      onClose={handleClose}
      subheaderText={subHeading}
    />
  );
};
